import { useEffect, useMemo, useState } from "react";
import {
  Button,
  CheckboxCustom,
  Img,
  Input,
  List,
  Radio,
  Text,
} from "components";
import DesignMyOwnCakeQuestionOneHeader from "components/DesignMyOwnCakeQuestionOneHeader";
import Footer1 from "components/Footer1";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import StepProgress from "components/StepProgress";
import globalRequest from "../../utils/global/globalRequest";
import { useDispatch, useSelector } from "react-redux";
import { changeLoader } from "../../redux/reducers/loader";
import * as API from "../../utils/helper/Enum";
import { setSnackbar } from "../../redux/reducers/snackbar";
import * as CUSTOM from "../../utils/helper/custom";
import { InputAdornment } from "@material-ui/core";
import EmptyAll from "pages/EmptyList";
import { ImageCropper } from "popups/ImageCropper";
import { getAuth } from "../../redux/reducers/loginData";
import DesignMyOwnCakeSummaryPage from "pages/DesignMyOwnCakeSummary";
import { useNavigate } from "react-router-dom";
import {
  addDeleteGetLocalStorage,
  storageKeys,
} from "../../utils/global/localData";
import { get } from "lodash";
import Footer from "components/Footer";
import CUSTOM_CAKE_IDS from "config/customCakeIds";

const Box = styled.div`
  ${({ isSelected }) =>
    isSelected &&
    ` 
      border: 2px solid #771237 !important;
      background-color: #FFF5F8;
`}
`;

// Import Google Fonts
const GlobalStyle = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600;700&display=swap');
`;

// Main Page Container
const PageWrapper = styled.div`
  background: #FEFBFC;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

// Header Section
const Header = styled.div`
  background: white;
  padding: 32px 5%;
  text-align: start;
  border-bottom: 1px solid #F0F0F0;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const PageTitle = styled.h1`
  font-family: 'Unbounded', Georgia, serif;
  font-size: 44px;
  font-weight: 700;
  color: #771237;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const PageSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 400;
  color: #8D4660;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Progress Stepper Section (Desktop only)
const StepperSection = styled.div`
  background: #FEFBFC;
  padding: 32px 20px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StepperWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0px;
  overflow-x: auto;
  padding: 10px 0;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 100px;
  position: relative;
`;

const StepCircle = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isCompleted, $isActive }) =>
    $isActive ? '#FFE8F0' : $isCompleted ? '#771237' : '#F5F5F5'};
  border: 3px solid ${({ $isCompleted, $isActive }) =>
    $isActive ? '#771237' : $isCompleted ? '#771237' : '#E5E5E5'};
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  box-shadow: ${({ $isActive }) =>
    $isActive ? '0 4px 16px rgba(119, 18, 55, 0.2)' : 'none'};
`;

const StepIconImg = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  filter: ${({ $isActive }) => $isActive ? 'brightness(0) invert(1)' : 'none'};
`;

const StepNumber = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ $isCompleted, $isActive }) =>
    $isCompleted ? 'white' : $isActive ? '#771237' : '#AAAAAA'};
`;

const CheckMark = styled.div`
  color: white;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
`;

const StepLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $isCompleted, $isActive }) =>
    $isCompleted || $isActive ? '#771237' : '#999999'};
  text-align: center;
  white-space: nowrap;
  letter-spacing: 0.3px;
`;

const StepLine = styled.div`
  height: 3px;
  width: 60px;
  background: ${({ $isCompleted }) => ($isCompleted ? '#771237' : '#E5E5E5')};
  margin-top: -15%;
  transition: all 0.3s ease;
  flex-shrink: 0;
`;

// Mobile Step Header
const MobileStepHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    background: white;
    border-bottom: 1px solid #F0F0F0;
  }
`;

const MobileStepInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MobileStepIcon = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const MobileStepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileStepTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
`;

const MobileStepCounter = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #8D4660;
`;

const ViewDetailsBtn = styled.button`
  background: white;
  border: 2px solid #771237;
  color: #771237;
  padding: 10px 20px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #FFF5F8;
  }

  &:after {
    content: '→';
    font-size: 16px;
  }
`;

// Mobile Preview Modal
const MobilePreviewModal = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    align-items: flex-end;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MobilePreviewContent = styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const MobilePreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #F0F0F0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const MobilePreviewTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1A1A1A;
  margin: 0;
`;

const CloseModalBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #F5F5F5;
  border: none;
  color: #666;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: #771237;
    color: white;
  }
`;

const MobilePreviewBody = styled.div`
  padding: 24px;
`;

// Main Content Area
const ContentArea = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 40px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    padding: 0 0 80px;
  }
`;

// Left Panel - Question Card
const QuestionPanel = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 0;
    box-shadow: none;
  }
`;

const QuestionContent = styled.div`
  padding: 48px;

  @media (max-width: 640px) {
    padding: 20px 16px;
  }
`;

const QuestionHeader = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 400;
  color: #1A1A1A;
  margin: 0 0 12px 0;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 16px;
    font-weight: 400;
    color: #333333;
    margin: 0 0 16px 0;
  }
`;

// Pinterest Browse Banner
const PinterestBanner = styled.div`
  background: linear-gradient(135deg, #FFF5F8 0%, #FFE8F0 100%);
  border: 2px dashed #FFB8D4;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    background: white;
    border: 1px solid #E8E8E8;
    border-radius: 12px;
    padding: 16px;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
`;

const PinterestIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    box-shadow: none;
  }
`;

const PinterestImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PinterestTextArea = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

const PinterestTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 2px;
    line-height: 1.4;
  }
`;

const PinterestSubtext = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #727783;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 12px;
    color: #999999;
    line-height: 1.3;
  }
`;

const BrowseBtn = styled.a`
  background: #771237;
  padding: 12px 28px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: inline-block;

  &:hover {
    background: #A00039;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(119, 18, 55, 0.3);
  }

  @media (max-width: 768px) {
    background: transparent;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    transform: rotate(-45deg)
    height: 24px;
    color: #666666;
    font-size: 18px;

    &:hover {
      background: transparent;
      transform: none;
      box-shadow: none;
      color: #771237;
    }

    &:after {
      content: '→';
    }
  }
`;

const BrowseBtnText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

// Form Elements
const FormLabel = styled.label`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #6D737E;
  display: block;
  margin-bottom: 12px;
  letter-spacing: 0.1px;

  @media (max-width: 768px) {
    font-size: 13px;
    color: #666666;
    margin-bottom: 8px;
  }
`;

const RequiredStar = styled.span`
  color: #771237;
  margin-left: 2px;
`;

// Upload Zone
const UploadZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #D0D0D0;
  border-radius: 16px;
  padding: 56px 32px;
  cursor: pointer;
  transition: all 0.25s ease;
  background: #FAFAFA;
  margin-bottom: 36px;
  position: relative;

  &:hover {
    border-color: #771237;
    background: #FFF8FA;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(119, 18, 55, 0.1);
  }

  input {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 32px 16px;
    border-radius: 12px;
    margin-bottom: 20px;
    background: #FAFAFA;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(119, 18, 55, 0.08);
    }
  }
`;

const UploadIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    margin-bottom: 12px;
  }
`;

const UploadIconImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const UploadMainText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #771237;
  margin-bottom: 8px;
  letter-spacing: -0.2px;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 4px;
  }
`;

const UploadSubText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #727783;
  letter-spacing: 0.1px;

  @media (max-width: 768px) {
    font-size: 12px;
    text-align: center;
    color: #999999;
  }
`;

// Image Grid for uploaded images
const UploadedImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 16px;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const ImageItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #F0F0F0;
  transition: all 0.2s ease;

  &:hover {
    border-color: #771237;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(119, 18, 55, 0.15);
  }

  @media (max-width: 768px) {
    border-radius: 8px;
    border: 1px solid #E8E8E8;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(119, 18, 55, 0.1);
    }
  }
`;

const UploadedImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.75);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: #771237;
    transform: scale(1.15);
  }

  @media (max-width: 768px) {
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    font-size: 16px;

    &:hover {
      transform: scale(1.1);
    }
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #E0E0E0;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #A2A6AE;
  transition: all 0.2s ease;
  margin-bottom: 36px;
  background: white;

  &::placeholder {
    color: #AAAAAA;
  }

  &:focus {
    outline: none;
    border-color: #771237;
    box-shadow: 0 0 0 4px rgba(119, 18, 55, 0.1);
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 14px;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid #E0E0E0;

    &:focus {
      box-shadow: 0 0 0 3px rgba(119, 18, 55, 0.08);
    }
  }
`;

const NavButtonsContainer = styled.div`
  display: flex;
  gap: 20px;
  padding: 36px 48px;
  background: white;
  border-top: 1px solid #F5F5F5;

  @media (max-width: 640px) {
    flex-direction: column;
    padding: 24px;
  }
`;

const NavBtn = styled.button`
  flex: 1;
  padding: 18px 36px;
  border-radius: 30px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  border: none;
  letter-spacing: -0.3px;

  ${({ $variant }) =>
    $variant === 'secondary'
      ? `
    background: white;
    border: 2px solid #E0E0E0;
    color: #666666;
    
    &:hover:not(:disabled) {
      background: #F8F8F8;
      border-color: #771237;
      color: #771237;
      transform: translateY(-2px);
    }
  `
      : `
    background: linear-gradient(135deg, #771237 0%, #A00039 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(119, 18, 55, 0.25);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #A00039 0%, #771237 100%);
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(119, 18, 55, 0.35);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const PreviewPanel = styled.div`
  background: white;
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 24px;

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const PreviewHeader = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1A1A1A;
  margin: 0 0 8px 0;
  letter-spacing: -0.4px;
`;

const PreviewDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #999999;
  margin: 0 0 28px 0;
  line-height: 1.6;
`;

const PreviewDivider = styled.div`
  height: 1px;
  background: #F0F0F0;
  margin: 24px 0;
`;

const PreviewBox = styled.div`
  background: #F9F9F9;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const PreviewBoxTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #666666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const PreviewBoxValue = styled.div`
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  color: #A2A6AE;
  line-height: 28px;
  word-break: break-word;
`;

const PreviewImageContainer = styled.div`
  background: linear-gradient(160deg, #F8F8F8 20%, #F9E5EC 40%, #F0F0F0 100%);
  border-radius: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
`;

const PreviewCakeImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  height: 240px;
  filter: drop-shadow(0 8px 24px rgba(119, 18, 55, 0.15));
`;

const PreviewSizeLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
  text-align: center;
  letter-spacing: 0.2px;
  margin-top: 16px;
`;

const TotalContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  margin-top: 24px;
  border-top: 2px solid #F0F0F0;
`;

const TotalText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1A1A1A;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TotalPrice = styled.div`
  font-family: 'Unbounded', sans-serif;
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: 700;
  color: #771237;
  letter-spacing: -0.5px;
`;

// Option Item
const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  width: 100%;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const OptionBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 3px solid ${({ $isSelected }) => ($isSelected ? '#771237' : '#E8E8E8')};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
  box-shadow: ${({ $isSelected }) => 
    $isSelected ? '0 6px 20px rgba(119, 18, 55, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.06)'};

  &:hover {
    border-color: #771237;
    transform: translateY(-6px);
    box-shadow: 0 8px 24px rgba(119, 18, 55, 0.25);
  }
`;

const OptionImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 24px;
`;

const OptionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;

  ${OptionBox}:hover & {
    transform: scale(1.08);
  }
`;

const OptionInfo = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ $isSelected }) => ($isSelected ? '#FFF5F8' : 'white')};
`;

const OptionLeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

const OptionTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionName = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
  letter-spacing: -0.3px;
`;

const OptionSubtext = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #666666;
`;

const OptionPriceTag = styled.div`
  font-family: 'Unbounded', sans-serif;
  font-size: 18px;
  display: flex;
  align-items: center;
  font-weight: 700;
  color: #1A1A1A;
  letter-spacing: -0.3px;
  white-space: nowrap;
`;

const QuestionSubtext = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #666666;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const SarIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 6px;
  object-fit: contain;
`;

const QuantityOptionBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 3px solid ${({ $isSelected }) => ($isSelected ? '#771237' : '#E8E8E8')};
  border-radius: 20px;
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
  box-shadow: ${({ $isSelected }) => 
    $isSelected ? '0 6px 20px rgba(119, 18, 55, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.06)'};

  &:hover {
    border-color: #771237;
    transform: translateY(-6px);
    box-shadow: 0 8px 24px rgba(119, 18, 55, 0.25);
  }
`;

const QuantityControlArea = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ $isSelected }) => ($isSelected ? '#FFF5F8' : 'white')};
`;

const QuantityLeftSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const QuantityCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  border: 1px solid #A2A6AE;
  border-radius: 30px;
  padding: 4px 12px;
  justify-content: center;
`;

const CounterButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 2px solid #E0E0E0;
  background: white;
  color: #771237;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover:not(:disabled) {
    border-color: #771237;
    background: #FFF5F8;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const CounterValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1A1A1A;
  min-width: 40px;
  text-align: center;
`;

const QuantitySubtext = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #999999;
  margin-top: 4px;
`;

// Message Input Components
const MessageInputBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  border: 3px solid ${({ $hasMessage }) => ($hasMessage ? '#771237' : '#E8E8E8')};
  border-radius: 20px;
  transition: all 0.3s ease;
  background: white;
  box-shadow: ${({ $hasMessage }) => 
    $hasMessage ? '0 6px 20px rgba(119, 18, 55, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.06)'};
  margin-bottom: 24px;
`;

const MessageLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 16px;
  border: 2px solid #E0E0E0;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #1A1A1A;
  resize: vertical;
  transition: all 0.2s ease;
  margin-top: 12px;

  &::placeholder {
    color: #AAAAAA;
  }

  &:focus {
    outline: none;
    border-color: #771237;
    box-shadow: 0 0 0 4px rgba(119, 18, 55, 0.1);
  }
`;

const STORAGE_KEYS = {
  CAKE_DATA: 'custom_cake_data',
  SELECTED_DATA: 'custom_cake_selected',
  IMAGES: 'custom_cake_images',
  REFERENCE: 'custom_cake_reference',
  TEXT: 'custom_cake_text',
  ACTIVE_STEP: 'custom_cake_step',
  COMPLETED_STEPS: 'custom_cake_completed',
  TOTAL_PRICE: 'custom_cake_price',
  SELECTED_SHAPE: 'custom_cake_shape'
};

const STATIC_QUESTIONS = [
  {
    id: 1,
    name: "Choose the perfect size for your celebration",
    type: "single",
    min: 1,
    max: 1,
    uiOrder: 1,
    envGroupId: CUSTOM_CAKE_IDS.SIZE_GROUP_ID,
    options: [
      { id: 11, name: "5-inch", subtext: "Round shape", price: 99, image: "/images/roundCake1.png",
        envItemId: CUSTOM_CAKE_IDS.SIZE_5INCH_ID,
      },
      {
        id: 12,
        name: "6-inch",
        subtext: "Various shapes offered",
        price: 299,
        image: "/images/roundCake1.png",
        envItemId: CUSTOM_CAKE_IDS.SIZE_6INCH_ID,
      },
      {
        id: 13,
        name: "8-inch",
        subtext: "Round shape",
        price: 399,
        image: "/images/roundCake1.png",
        envItemId: CUSTOM_CAKE_IDS.SIZE_8INCH_ID,
      }
    ]
  },
  {
    id: 2,
    name: "Choose your cake shape",
    type: "single",
    min: 1,
    max: 1,
    uiOrder: 2,
    envGroupId: CUSTOM_CAKE_IDS.SHAPE_GROUP_ID,
    options: [
      // { id: 21, name: "Square", price: 100, image: "/images/squareCake.png", shapeKey: "square", envItemId: null },
      {
        id: 22,
        name: "Round",
        price: 0,
        image: "/images/roundCake.png",
        shapeKey: "round",
        envItemId: CUSTOM_CAKE_IDS.SHAPE_ROUND_ID,
      },
      // { id: 23, name: "Heart", price: 240, image: "/images/heartCake.png", shapeKey: "heart", envItemId: CUSTOM_CAKE_IDS.SHAPE_HEART_ID }
    ]
  },
  {
    id: 3,
    name: "Select your cake flavor",
    type: "single",
    min: 1,
    max: 1,
    uiOrder: 3,
    envGroupId: CUSTOM_CAKE_IDS.FLAVOR_GROUP_ID,
    options: [
      // {
      //   id: 31,
      //   name: "Vanilla",
      //   price: 51,
      //   envItemId: null,
      //   images: { square: "/images/flawer1.png", round: "/images/venillaFlawerRound.png", heart: "/images/venillaFlawerHeart.png" }
      // },
      {
        id: 32,
        name: "Chocolate",
        price: 0,
        envItemId: CUSTOM_CAKE_IDS.FLAVOR_CHOCOLATE_ID,
        images: {
          square: "/images/flawer2.png",
          round: "/images/chokoflawerRound.png",
          heart: "/images/chokoflawerHeart.png"
        }
      },
      { 
        id: 33, 
        name: "Strawberry", 
        price: 0, 
        envItemId: CUSTOM_CAKE_IDS.FLAVOR_STRAWBERRY_ID,
        images: {
          square: "/images/flawer3.png",
          round: "/images/strawberryFlawerRound.png",
          heart: "/images/strawberryFlawerHeart.png"
        }
      },
      // {
      //   id: 34,
      //   name: "Berry",
      //   price: 76,
      //   envItemId: null,
      //   images: { square: "/images/flawer4.png", round: "/images/berryFlawerRound.png", heart: "/images/berryFlawerHeart.png" }
      // }
    ]
  },
  {
    id: 4,
    name: "Select sugar dough 3D shapes (Optional Selection)",
    subtext: "Skip if you would not prefer one",
    type: "quantity",
    min: 0,
    max: 0,
    uiOrder: 4,
    envGroupId: CUSTOM_CAKE_IDS.D3_GROUP_ID,
    envQuantityMap: {
      0: CUSTOM_CAKE_IDS.D3_0SHAPE_ID,
      1: CUSTOM_CAKE_IDS.D3_1SHAPE_ID,
      2: CUSTOM_CAKE_IDS.D3_2SHAPE_ID,
      3: CUSTOM_CAKE_IDS.D3_3SHAPE_ID,
    },
    options: [
      { 
        id: 41, 
        name: "3D Pieces", 
        subtext: "Choose the 3D shapes that you want on the cake",
        price: 40,  
        envItemId: CUSTOM_CAKE_IDS.D3_1SHAPE_ID,
        images: {
          square: "/images/3DSquare.png",
          round: "/images/3DRound.png",
          heart: "/images/3DHeart.png"
        },
        needsShape: true 
      }
    ]
  },
  {
    id: 5,
    name: "Add glitter effects (Optional Selection)",
    subtext: "Skip if you prefer a classic look",
    type: "single",
    min: 0,
    max: 0,
    uiOrder: 5,
    envGroupId: CUSTOM_CAKE_IDS.GLITTER_GROUP_ID,
    options: [
      { 
        id: 51, 
        name: "Golden Color", 
        price: 30, 
        envItemId: CUSTOM_CAKE_IDS.GLITTER_GOLDEN_ID,
        images: {
          square: "/images/glitterGolden.png",
          round: "/images/glitterGoldenRound.png",
          heart: "/images/glitterGoldenHeart.png"
        }
      },
      { 
        id: 52, 
        name: "Silver Color", 
        price: 30, 
        envItemId: CUSTOM_CAKE_IDS.GLITTER_SILVER_ID,
        images: {
          square: "/images/glitterSilver.png",
          round: "/images/glitterSilverRound.png",
          heart: "/images/glitterSilverHeart.png"
        }
      },
      // { id: 53, name: "Pinky Color", price: 10, envItemId: null, images: {...} },
      // { id: 54, name: "Bronzy Color", price: 10, envItemId: null, images: {...} },
      // { id: 55, name: "Navy Color", price: 10, envItemId: null, images: {...} },
      // { id: 56, name: "Baby Blue Color", price: 10, envItemId: null, images: {...} }
    ]
  },
  {
    id: 6,
    name: "Add ribbon (Optional Selection)",
    subtext: "You can add up to 4 ribbons",
    type: "quantity",
    min: 0,
    max: 0,
    uiOrder: 6,
    envGroupId: CUSTOM_CAKE_IDS.RIBBON_GROUP_ID,
    envQuantityMap: {
      0: CUSTOM_CAKE_IDS.RIBBON_NO_ID,
      1: CUSTOM_CAKE_IDS.RIBBON_1PC_ID,
      2: CUSTOM_CAKE_IDS.RIBBON_2PC_ID,
      3: CUSTOM_CAKE_IDS.RIBBON_3PC_ID,
      4: CUSTOM_CAKE_IDS.RIBBON_4PC_ID,
    },
    options: [
      { 
        id: 61, 
        name: "Ribbons", 
        price: 10, 
        envItemId: CUSTOM_CAKE_IDS.RIBBON_1PC_ID,
        images: {
          square: "/images/ribbonSquare.png",
          round: "/images/ribbonRound.png",
          heart: "/images/ribbonHeart.png"
        },
        needsShape: true 
      }
    ]
  },
  {
    id: 7,
    name: "Add a message card (Optional Selection)",
    subtext: "Skip if you prefer to deliver without a message",
    type: "message",
    min: 0,
    max: 0,
    uiOrder: 7,
    envGroupId: CUSTOM_CAKE_IDS.MESSAGE_GROUP_ID,
    options: [
      { 
        id: 71, 
        name: "Your Message", 
        price: 10, 
        image: "/images/specialGift.png",
        envItemId: CUSTOM_CAKE_IDS.MESSAGE_PRINTING_ID,
      }
    ]
  }
];

const DesignMyOwnCakeQuestionOnePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let dispatch = useDispatch();
  const [imageForCrop, setImageForCrop] = useState("");
  const [allSelectedImages, setAllSelectedImages] = useState([]);
  const [settingData, setSettingData] = useState({ searchLink: "https://in.pinterest.com/search/pins/?q=custom%20cake&rs=typed" });
  const [reference, setReference] = useState("");
  const [textOnCake, setTextOnCake] = useState("");
  const [selectedData, setSelectedData] = useState([]);
  const [activeId, setActiveId] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedShape, setSelectedShape] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [messageCardText, setMessageCardText] = useState("");
  const [allInputData, setAllInputData] = useState({});
  const [backendQuestions, setBackendQuestions] = useState([]);
  const [idMapping, setIdMapping] = useState({});
  const [apiStatus, setApiStatus] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const auth = useSelector(getAuth);

  // Step labels
  const stepLabels = [
    "Reference",
    "Size",
    "Shape",
    "Flavor",
    "Design 3D",
    "Glitter",
    "Ribbon",
    "Message"
  ];

  // Get step icon image
  const getStepIcon = (index) => {
    return `/images/step${index + 1}.png`;
  };

  // Get current step name
  const getCurrentStepName = () => {
    if (activeId === -1) return "Reference";
    if (activeId >= 0 && activeId < STATIC_QUESTIONS.length) {
      return stepLabels[activeId + 1] || STATIC_QUESTIONS[activeId].name;
    }
    return "";
  };

  const getCurrentStepDisplay = () => {
    const currentStep = activeId + 2;
    return `Step ${currentStep} of ${stepLabels.length}`;
  };

  const getQuestionList = async () => {
    try {
      dispatch(changeLoader(true));
      let response = await globalRequest(
        `${API.CUSTOMCAKE.CUSTOM_CAKE_QUESTIONS_LIST}`,
        "get",
        {},
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));
      
      if (response?.status == "SUCCESS") {
        const backendData = response?.data;
        setBackendQuestions(backendData);
        setSettingData(response?.setting_data);
        /*
        const mapping = {};
        backendData.forEach((backendQuestion, index) => {
          const questionName = CUSTOM.getdataByLangKey(
            backendQuestion.modifierGroupLocales,
            "",
            "name"
          );
          const staticQuestion = STATIC_QUESTIONS[index];
          if (staticQuestion) {
            mapping[staticQuestion.id] = {
              backendGroupId: backendQuestion.id,
              options: {}
            };
            
            backendQuestion.modifierGroupItems.forEach((backendItem, optIndex) => {
              const optionName = CUSTOM.getdataByLangKey(
                backendItem.modifierGroupItemLocales,
                "",
                "name"
              );
              
              const staticOption = staticQuestion.options[optIndex];
              
              if (staticOption) {
                mapping[staticQuestion.id].options[staticOption.id] = backendItem.id;
              } else {
                console.warn(`    No static option found for index ${optIndex}`);
              }
            });
          }
        });
        
        setIdMapping(mapping);
        */

        setIdMapping({ envBased: true });
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message,
          snackbarState: "error",
        })
      );
    }
    setApiStatus(true);
  };

  const getOptionImage = (option, questionId) => {
    if (option.images && selectedShape) {
      const shapeLower = selectedShape.toLowerCase();
      
      if (shapeLower.includes('square')) {
        return option.images.square;
      }
      if (shapeLower.includes('round')) {
        return option.images.round;
      }
      if (shapeLower.includes('heart')) {
        return option.images.heart;
      }
    }
    
    if (option.needsShape && selectedShape) {
      const shapeLower = selectedShape.toLowerCase();
      
      if (questionId === 4) {
        if (shapeLower.includes('square')) return '/images/3DSquare.png';
        if (shapeLower.includes('round')) return '/images/3DRound.png';
        if (shapeLower.includes('heart')) return '/images/3DHeart.png';
      }
      
      if (questionId === 6) {
        if (shapeLower.includes('square')) return '/images/ribbonSquare.png';
        if (shapeLower.includes('round')) return '/images/ribbonRound.png';
        if (shapeLower.includes('heart')) return '/images/ribbonHeart.png';
      }
    }
    
    if (option.image) {
      return option.image;
    }
    
    if (option.images) {
      return option.images.square;
    }
    
    return '/images/roundCake.png';
  };

  // Get preview image
  const getPreviewImage = () => {
    const currentQuestion = STATIC_QUESTIONS[activeId];
    
    if (activeId === 3 && selectedShape) {
      const shapeLower = selectedShape.toLowerCase();
      if (shapeLower.includes('square')) return '/images/3DSquare.png';
      if (shapeLower.includes('round')) return '/images/3DRound.png';
      if (shapeLower.includes('heart')) return '/images/3DHeart.png';
    }
    
    // Preview for flavor step (Step 2 - index 2)
    if (activeId === 2) {
      const flavorGroup = selectedData.find(group => group.group_id === 3);
      if (flavorGroup && flavorGroup.items.length > 0 && selectedShape) {
        const flavorQuestion = STATIC_QUESTIONS.find(q => q.id === 3);
        if (flavorQuestion) {
          const selectedFlavor = flavorQuestion.options.find(
            opt => opt.id === flavorGroup.items[0]
          );
          if (selectedFlavor && selectedFlavor.images) {
            const shapeLower = selectedShape.toLowerCase();
            if (shapeLower.includes('square')) return selectedFlavor.images.square;
            if (shapeLower.includes('round')) return selectedFlavor.images.round;
            if (shapeLower.includes('heart')) return selectedFlavor.images.heart;
          }
        }
      }
    }
    
    // Preview for glitter step (Step 4 - index 4)
    if (activeId === 4) {
      const glitterGroup = selectedData.find(group => group.group_id === 5);
      if (glitterGroup && glitterGroup.items.length > 0 && selectedShape) {
        const glitterQuestion = STATIC_QUESTIONS.find(q => q.id === 5);
        if (glitterQuestion) {
          const selectedGlitter = glitterQuestion.options.find(
            opt => opt.id === glitterGroup.items[0]
          );
          if (selectedGlitter && selectedGlitter.images) {
            const shapeLower = selectedShape.toLowerCase();
            if (shapeLower.includes('square')) return selectedGlitter.images.square;
            if (shapeLower.includes('round')) return selectedGlitter.images.round;
            if (shapeLower.includes('heart')) return selectedGlitter.images.heart;
          }
        }
      }
    }
    
    if (activeId === 5 && selectedShape) {
      const shapeLower = selectedShape.toLowerCase();
      if (shapeLower.includes('square')) return '/images/ribbonSquare.png';
      if (shapeLower.includes('round')) return '/images/ribbonRound.png';
      if (shapeLower.includes('heart')) return '/images/ribbonHeart.png';
    }
    
    if (selectedShape) {
      const shapeLower = selectedShape.toLowerCase();
      if (shapeLower.includes('square')) return '/images/squareCake.png';
      if (shapeLower.includes('round')) return '/images/roundCake.png';
      if (shapeLower.includes('heart')) return '/images/heartCake.png';
    }
    
    return '/images/roundCake.png';
  };

  // Check if message card has content
  const hasMessageCard = () => {
    return messageCardText.trim().length > 0;
  };

  // Handle quantity increment
  const handleQuantityIncrement = (questionId, optionId) => {
    const key = `${questionId}_${optionId}`;
    const currentQty = quantities[key] || 0;
    setQuantities({
      ...quantities,
      [key]: currentQty + 1
    });
  };

  // Handle quantity decrement
  const handleQuantityDecrement = (questionId, optionId) => {
    const key = `${questionId}_${optionId}`;
    const currentQty = quantities[key] || 0;
    if (currentQty > 0) {
      setQuantities({
        ...quantities,
        [key]: currentQty - 1
      });
    }
  };

  const getQuantity = (questionId, optionId) => {
    const key = `${questionId}_${optionId}`;
    return quantities[key] || 0;
  };

  const getTotalPrice = () => {
    let total = 0;
    
    total += selectedData.reduce((sum, groupData) => {
      const question = STATIC_QUESTIONS.find(q => q.id === groupData.group_id);
      if (question) {
        groupData.items.forEach(itemId => {
          const option = question.options.find(opt => opt.id === itemId);
          if (option) {
            sum += option.price;
          }
        });
      }
      return sum;
    }, 0);
    
    Object.keys(quantities).forEach(key => {
      const [questionId, optionId] = key.split('_').map(Number);
      const qty = quantities[key];
      
      if (qty > 0) {
        const question = STATIC_QUESTIONS.find(q => q.id === questionId);
        if (question) {
          const option = question.options.find(opt => opt.id === optionId);
          if (option) {
            total += option.price * qty;
          }
        }
      }
    });
    
    if (hasMessageCard()) {
      const messageQuestion = STATIC_QUESTIONS.find(q => q.id === 7);
      if (messageQuestion && messageQuestion.options[0]) {
        total += messageQuestion.options[0].price;
      }
    }
    
    return parseFloat(total.toFixed(2));
  };

  // Save to localStorage
  const saveToLocalStorage = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.SELECTED_DATA, JSON.stringify(selectedData));
      localStorage.setItem(STORAGE_KEYS.IMAGES, JSON.stringify(allSelectedImages));
      localStorage.setItem(STORAGE_KEYS.REFERENCE, reference);
      localStorage.setItem(STORAGE_KEYS.TEXT, textOnCake);
      localStorage.setItem(STORAGE_KEYS.ACTIVE_STEP, activeId.toString());
      localStorage.setItem(STORAGE_KEYS.COMPLETED_STEPS, JSON.stringify(completedSteps));
      localStorage.setItem(STORAGE_KEYS.TOTAL_PRICE, totalPrice.toString());
      localStorage.setItem('custom_cake_quantities', JSON.stringify(quantities));
      localStorage.setItem('custom_cake_message', messageCardText);
      if (selectedShape) {
        localStorage.setItem(STORAGE_KEYS.SELECTED_SHAPE, selectedShape);
      }
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  };

  // Load from localStorage
  const loadFromLocalStorage = () => {
    try {
      const savedSelectedData = localStorage.getItem(STORAGE_KEYS.SELECTED_DATA);
      const savedImages = localStorage.getItem(STORAGE_KEYS.IMAGES);
      const savedReference = localStorage.getItem(STORAGE_KEYS.REFERENCE);
      const savedText = localStorage.getItem(STORAGE_KEYS.TEXT);
      const savedStep = localStorage.getItem(STORAGE_KEYS.ACTIVE_STEP);
      const savedCompleted = localStorage.getItem(STORAGE_KEYS.COMPLETED_STEPS);
      const savedPrice = localStorage.getItem(STORAGE_KEYS.TOTAL_PRICE);
      const savedShape = localStorage.getItem(STORAGE_KEYS.SELECTED_SHAPE);
      const savedQuantities = localStorage.getItem('custom_cake_quantities');
      const savedMessage = localStorage.getItem('custom_cake_message');

      if (savedSelectedData) setSelectedData(JSON.parse(savedSelectedData));
      if (savedImages) setAllSelectedImages(JSON.parse(savedImages));
      if (savedReference) setReference(savedReference);
      if (savedText) setTextOnCake(savedText);
      if (savedStep) setActiveId(parseInt(savedStep));
      if (savedCompleted) setCompletedSteps(JSON.parse(savedCompleted));
      if (savedPrice) setTotalPrice(parseFloat(savedPrice));
      if (savedShape) setSelectedShape(savedShape);
      if (savedQuantities) setQuantities(JSON.parse(savedQuantities));
      if (savedMessage) setMessageCardText(savedMessage);
    } catch (e) {
      console.error('Error loading from localStorage:', e);
    }
  };

  // Add cart items state
  const addCartItemsState = async (question, optionId) => {
    let existingModifierIndex = selectedData?.findIndex(
      (modifier) => modifier?.group_id === question?.id
    );
    let updatedSelectedData = [];
    
    // Track shape selection
    if (question.id === 2) {
      const option = question.options.find(opt => opt.id === optionId);
      if (option && option.name) {
        setSelectedShape(option.name);
      }
    }
    
    if (existingModifierIndex === -1) {
      const newModifier = {
        group_id: question?.id,
        items: [optionId],
      };
      await setSelectedData((prevSelectedData) => {
        const newSelectedData = [...prevSelectedData, newModifier];
        updatedSelectedData = newSelectedData;
        existingModifierIndex = 0;
        return newSelectedData;
      });
    } else {
      if (checkedCheckbox(question, optionId) == false) {
        let max = question?.max;
        if (question?.type == "single") {
          max = 1;
        }
        let dataGetLength = selectedData[existingModifierIndex]?.items.length;
        let removeLastItem = false;
        if (dataGetLength == max && max != 0) {
          removeLastItem = true;
        }
        let newElement = optionId;
        const updatedData = selectedData.map((item) => {
          if (item.group_id === question?.id) {
            if (removeLastItem) {
              let itemArray = [...item.items];
              itemArray.pop();
              itemArray.push(newElement);
              return {
                ...item,
                items: itemArray,
              };
            } else {
              return {
                ...item,
                items: [...item.items, newElement],
              };
            }
          }
          return item;
        });
        updatedSelectedData = updatedData;
        setSelectedData(updatedData);
      } else {
        let elementToRemove = optionId;
        const updatedData = selectedData.map((item) => {
          if (item.group_id === question?.id) {
            return {
              ...item,
              items: item.items.filter((item) => item !== elementToRemove),
            };
          }
          return item;
        });
        updatedSelectedData = updatedData;
        setSelectedData(updatedData);
      }
    }
  };

  const checkedCheckbox = (question, optionId) => {
    const existingModifierIndex = selectedData?.findIndex(
      (modifier) => modifier?.group_id === question?.id
    );
    if (existingModifierIndex !== -1) {
      let dataGetLength = selectedData[existingModifierIndex]?.items;
      if (dataGetLength.includes(optionId)) {
        return true;
      }
    }
    return false;
  };

  const isCurrentStepValid = () => {
    if (activeId === -1) {
      return (allSelectedImages.length > 0 || reference) && 
             (!reference || CUSTOM.isValidField(reference, "url"));
    }

    if (activeId >= 0 && activeId < STATIC_QUESTIONS.length) {
      const currentQuestion = STATIC_QUESTIONS[activeId];
      let min = currentQuestion?.min;
      if (currentQuestion?.type == "single" && currentQuestion?.min > 0) {
        min = 1;
      }
      
      if (currentQuestion?.type == "message") {
        return true;
      }
      
      let existingModifierIndex = selectedData?.findIndex(
        (modifier) => modifier?.group_id === currentQuestion?.id
      );
      
      let totalItems = 0;
      if (existingModifierIndex !== -1) {
        totalItems = selectedData[existingModifierIndex].items.length;
      }
      
      if (currentQuestion?.type == "quantity") {
        Object.keys(quantities).forEach(key => {
          const [questionId] = key.split('_').map(Number);
          if (questionId === currentQuestion.id) {
            totalItems += quantities[key];
          }
        });
      }
      
      return totalItems >= min;
    }

    return true;
  };

  const handleNextStep = () => {
    if (!isCurrentStepValid()) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: "Please complete the current step before proceeding",
          snackbarState: "error",
        })
      );
      return;
    }

    if (!completedSteps.includes(activeId)) {
      setCompletedSteps([...completedSteps, activeId]);
    }

    if (activeId < STATIC_QUESTIONS.length - 1) {
      setActiveId(activeId + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (activeId === STATIC_QUESTIONS.length - 1) {
      handleCompleteOrder();
    }
  };

  const handlePreviousStep = () => {
    if (activeId > -1) {
      setActiveId(activeId - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImageChange = (e) => {
    e.preventDefault();
    let file = e.target.files[0];
    let imageError = CUSTOM.checkFileExtention(file.name, "png|jpg|jpeg", "", "");
    if (imageError) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: imageError,
          snackbarState: "error",
        })
      );
      document.getElementById("image-upload-field").value = "";
      return false;
    }
    let reader = new FileReader();
    reader.onloadend = () => {
      getCompressedImage(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
    document.getElementById("image-upload-field").value = "";
  };

  const getCompressedImage = (base64Url) => {
    const outputQuality = 0.9;
    const maxSizeInBytes = 0.7 * 1024 * 1024;

    const img = new Image();
    img.src = base64Url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const tryCompress = (quality) => {
        return new Promise((resolve) => {
          canvas.toBlob(
            (blob) => {
              if (blob.size <= maxSizeInBytes || quality <= 0.1) {
                resolve(blob);
              } else {
                tryCompress(quality - 0.1).then(resolve);
              }
            },
            "image/jpeg",
            quality
          );
        });
      };

      tryCompress(outputQuality).then((blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          uploadImageToServer(reader.result);
        };
        reader.readAsDataURL(blob);
      });
    };
  };

  const uploadImageToServer = async (base64Image) => {
    try {
      dispatch(changeLoader(true));
      let response = await globalRequest(
        `${API.CUSTOMCAKE.UPLOAD_IMAGE}`,
        "post",
        { image: base64Image },
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));
      
      if (response?.status == "SUCCESS") {
        const updatedImages = [...allSelectedImages, response?.data];
        setAllSelectedImages(updatedImages);
        setImageForCrop("");
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(changeLoader(false));
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message,
          snackbarState: "error",
        })
      );
    }
  };

  const getInputData = () => {
    const cake_details = [];


    selectedData.forEach(group => {
      const staticQuestion = STATIC_QUESTIONS.find(q => q.id === group.group_id);
      if (!staticQuestion || !staticQuestion.envGroupId) {
        console.warn(`No envGroupId for static question ${group.group_id}`);
        return;
      }

      const backendItems = group.items.map(staticItemId => {
        const option = staticQuestion.options.find(opt => opt.id === staticItemId);
        if (!option || !option.envItemId) {
          console.warn(`No envItemId for option ${staticItemId} in question ${group.group_id}`);
          return null;
        }
        return option.envItemId;
      }).filter(id => id !== null && id !== undefined && !isNaN(id));

      if (backendItems.length > 0) {
        cake_details.push({
          group_id: staticQuestion.envGroupId,
          items: backendItems
        });
      }
    });

    Object.keys(quantities).forEach(key => {
      const [staticQuestionId, staticOptionId] = key.split('_').map(Number);
      const qty = quantities[key];

      if (qty > 0) {
        const staticQuestion = STATIC_QUESTIONS.find(q => q.id === staticQuestionId);
        if (!staticQuestion || !staticQuestion.envGroupId) {
          console.warn(`No envGroupId for quantity question ${staticQuestionId}`);
          return;
        }

        let backendItemId = null;

        if (staticQuestion.envQuantityMap && staticQuestion.envQuantityMap[qty] !== undefined) {
          backendItemId = staticQuestion.envQuantityMap[qty];
        } else {
          const option = staticQuestion.options.find(opt => opt.id === staticOptionId);
          if (option && option.envItemId) {
            backendItemId = option.envItemId;
          }
        }

        if (backendItemId && !isNaN(backendItemId)) {
            const existingGroupIndex = cake_details.findIndex(
              g => g.group_id === staticQuestion.envGroupId
            );

            if (existingGroupIndex !== -1) {
              cake_details[existingGroupIndex].items = [backendItemId];
            } else {
              cake_details.push({
                group_id: staticQuestion.envGroupId,
                items: [backendItemId]
              });
            }
        } else {
          console.warn(`No backendItemId found for qty ${qty} in question ${staticQuestionId}`);
        }
      }
    });

    if (hasMessageCard()) {
      const messageQuestion = STATIC_QUESTIONS.find(q => q.id === 7);
      if (
        messageQuestion &&
        messageQuestion.envGroupId &&
        messageQuestion.options[0]?.envItemId &&
        !isNaN(messageQuestion.options[0].envItemId)
      ) {
        cake_details.push({
          group_id: messageQuestion.envGroupId,
          items: [messageQuestion.options[0].envItemId]
        });
      }
    }

    return {
      cake_details: cake_details,
      upload_image_links: allSelectedImages,
      image_link: reference,
      text_on_cake: textOnCake,
      message_card_text: messageCardText,
      id: allInputData?.id ? allInputData?.id : "",
    };
  };

  const processCustomization = (cakeDetails) => {
    try {
      const customizations = [];

      cakeDetails.forEach((group) => {
        const question = group?.modifierGroupLocales?.[0]?.name;
        group?.modifierGroupItems?.forEach((item) => {
          const answer = item?.modifierGroupItemLocales?.[0]?.name;
          if (question && answer) {
            customizations.push({ question, answer });
          }
        });
      });

      return customizations;
    } catch (error) {
      return [];
    }
  };

  // Save for later function
  const saveForLater = async () => {
    try {
      dispatch(changeLoader(true));
      
      const inputData = getInputData();
      
      if (!inputData || !inputData.cake_details || inputData.cake_details.length === 0) {
        dispatch(changeLoader(false));
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: "Unable to save. Please ensure all questions are loaded and selections are made.",
            snackbarState: "error",
          })
        );
        return;
      }
      
      let response = await globalRequest(
        API?.CUSTOMCAKE?.SAVE_FOR_LATTER,
        "post",
        { 
          cake_details: inputData.cake_details,
          upload_image_links: inputData.upload_image_links,
          image_link: inputData.image_link,
          text_on_cake: inputData.text_on_cake,
          message_card_text: inputData.message_card_text,
          id: inputData.id,
          gaData: CUSTOM.getGACookieValues() 
        },
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));
      
      if (response?.status == "SUCCESS") {
        const customization = processCustomization(response?.data?.cakeDetails);
        CUSTOM.trackCustomCakeSave({ cake_type: "Custom", customization: customization });
        
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "success",
          })
        );
        
        addDeleteGetLocalStorage(
          storageKeys.ORDER_TAB,
          "mycustomcake",
          "add",
          "single"
        );
        
        clearLocalStorage();
        
        navigate("/my-order");
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(changeLoader(false));
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message,
          snackbarState: "error",
        })
      );
    }
  };

  const placeOrder = async () => {
    try {
      dispatch(changeLoader(true));
      
      const inputData = getInputData();
      
      if (!inputData || !inputData.cake_details || inputData.cake_details.length === 0) {
        dispatch(changeLoader(false));
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: "Unable to process order. Please ensure all questions are loaded and selections are made.",
            snackbarState: "error",
          })
        );
        return;
      }
      
      let placeOrderInput = {
        customCakeId: 0,
        device_type: "web",
        customCakeDetails: {
          cake_details: inputData.cake_details,
          upload_image_links: inputData.upload_image_links,
          image_link: inputData.image_link,
          text_on_cake: inputData.text_on_cake,
          message_card_text: inputData.message_card_text,
        },
      };
      

      let response = await globalRequest(
        API?.CUSTOMCAKE?.PLACE_ORDER,
        "post",
        { ...placeOrderInput, gaData: CUSTOM.getGACookieValues() },
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));
      
      if (response?.status == "SUCCESS") {
        const customization = processCustomization(response?.cakeDetails);
        CUSTOM.trackCustomCakeCheckout({ cake_type: "Custom", customization: customization });
        
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "success",
          })
        );
        
        localStorage.removeItem("pendingOrderData");
        clearLocalStorage();
        
        navigate("/my-order");
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(changeLoader(false));
      console.error("Place order error:", e);
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message || "Failed to place order",
          snackbarState: "error",
        })
      );
    }
  };

  // Clear localStorage function
  const clearLocalStorage = () => {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    localStorage.removeItem('custom_cake_quantities');
    localStorage.removeItem('custom_cake_message');
  };

  const handleCompleteOrder = () => {
    if (!idMapping || !idMapping.envBased) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: "System is still loading. Please wait a moment and try again.",
          snackbarState: "error",
        })
      );
      return;
    }

    const currentPage = window.location.pathname;
    
    const inputData = getInputData();
    
    if (!inputData) {
      return;
    }

    const placeOrderInput = {
      customCakeId: 0,
      device_type: "web",
      totalPrice: CUSTOM.setPrice(totalPrice),
      customCakeDetails: {
        cake_details: inputData.cake_details,
        upload_image_links: inputData.upload_image_links,
        image_link: inputData.image_link,
        text_on_cake: inputData.text_on_cake,
        message_card_text: inputData.message_card_text,
      },
    };

    if (!auth || auth?.id === 0) {
      localStorage.setItem(
        "pendingOrderData",
        JSON.stringify({
          action: "placeOrder",
          page: currentPage,
          formData: placeOrderInput,
        })
      );
      navigate(`/login?redirect=${encodeURIComponent(currentPage)}`);
    } else {
      placeOrder();
    }
  };

  const getSelectedItemsForPreview = () => {
    const items = [];
    
    selectedData.forEach((selectedGroup) => {
      const question = STATIC_QUESTIONS.find((q) => q?.id === selectedGroup?.group_id);
      if (question) {
        selectedGroup.items.forEach((itemId) => {
          const option = question.options.find((opt) => opt?.id === itemId);
          if (option) {
            items.push({
              groupName: question.name,
              name: option.name,
              price: option.price,
              quantity: 1
            });
          }
        });
      }
    });
    
    Object.keys(quantities).forEach(key => {
      const [questionId, optionId] = key.split('_').map(Number);
      const qty = quantities[key];
      
      if (qty > 0) {
        const question = STATIC_QUESTIONS.find(q => q.id === questionId);
        if (question) {
          const option = question.options.find(opt => opt.id === optionId);
          if (option) {
            items.push({
              groupName: question.name,
              name: option.name,
              price: option.price,
              quantity: qty
            });
          }
        }
      }
    });
    
    if (hasMessageCard()) {
      const messageQuestion = STATIC_QUESTIONS.find(q => q.id === 7);
      if (messageQuestion && messageQuestion.options[0]) {
        items.push({
          groupName: messageQuestion.name,
          name: messageQuestion.options[0].name,
          price: messageQuestion.options[0].price,
          quantity: 1,
          isMessage: true
        });
      }
    }
    
    return items;
  };

  const renderPreviewContent = () => {
    return (
      <>
        <PreviewHeader>Live Preview</PreviewHeader>
        <PreviewDescription>
          Your custom cake will appear here as you make selections
        </PreviewDescription>

        {activeId === 6 ? (
          <PreviewImageContainer>
            <PreviewCakeImage
              src="/images/specialGift.png"
              alt="Message Card"
              onError={(e) => {
                e.target.src = '/images/specialGift.png';
              }}
              style={{ maxWidth: '350px' }}
            />
          </PreviewImageContainer>
        ) : (
          selectedData.length > 0 && (
            <PreviewImageContainer>
              <PreviewCakeImage
                src={getPreviewImage()}
                alt="Cake Preview"
                onError={(e) => {
                  e.target.src = '/images/roundCake.png';
                }}
              />
            </PreviewImageContainer>
          )
        )}

        {reference && (
          <>
            <PreviewDivider />
            <PreviewBox>
              <PreviewBoxTitle>Reference Link</PreviewBoxTitle>
              <PreviewBoxValue style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                {reference}
              </PreviewBoxValue>
            </PreviewBox>
          </>
        )}

        {textOnCake && (
          <PreviewBox>
            <PreviewBoxTitle>Text on Cake</PreviewBoxTitle>
            <PreviewBoxValue>{textOnCake}</PreviewBoxValue>
          </PreviewBox>
        )}

        {messageCardText && (
          <PreviewBox>
            <PreviewBoxTitle>Message Card</PreviewBoxTitle>
            <PreviewBoxValue>{messageCardText}</PreviewBoxValue>
          </PreviewBox>
        )}

        {/* Selected Items */}
        {getSelectedItemsForPreview().map((item, index) => (
          <PreviewBox key={index}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <PreviewBoxValue>
                  {item.name}
                  {item.quantity > 1 && ` (x${item.quantity})`}
                </PreviewBoxValue>
              </div>
              <OptionPriceTag>
                <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                {item.quantity > 1 ? item.price * item.quantity : item.price}
              </OptionPriceTag>
            </div>
          </PreviewBox>
        ))}

        <TotalContainer>
          <TotalText>Total</TotalText>
          <TotalPrice>
            <SarIcon src="/images/sarRed.svg" alt="SAR" />
            {CUSTOM.setPrice ? CUSTOM.setPrice(totalPrice) : totalPrice}
          </TotalPrice>
        </TotalContainer>
      </>
    );
  };

  const renderCurrentStep = () => {
    if (activeId === -1) {
      return (
        <>
          <QuestionContent>
            <QuestionHeader>Upload reference images</QuestionHeader>
            
            <PinterestBanner>
              <PinterestIconWrapper>
                <PinterestImage 
                  src="/images/pinterest.png"
                  alt="Pinterest"
                />
              </PinterestIconWrapper>
              <PinterestTextArea>
                <PinterestTitle>Browse our Pinterest cake collection for inspiration</PinterestTitle>
                <PinterestSubtext>Copy images or paste URLs below</PinterestSubtext>
              </PinterestTextArea>
              <BrowseBtn
                href={settingData?.searchLink || "https://in.pinterest.com/search/pins/?q=custom%20cake&rs=typed"}
                target="_blank"
                rel="noreferrer"
              >
                <BrowseBtnText>Browse</BrowseBtnText>
              </BrowseBtn>
            </PinterestBanner>

            {allSelectedImages.length > 0 && (
              <UploadedImagesGrid>
                {allSelectedImages.map((img, index) => (
                  <ImageItem key={index}>
                    <UploadedImg
                      src={img.includes("://") ? img : `${process.env.REACT_APP_BASEURL}${img}`}
                      alt={`Uploaded ${index + 1}`}
                    />
                    <RemoveBtn
                      onClick={() => {
                        const updatedImages = allSelectedImages.filter((_, i) => i !== index);
                        setAllSelectedImages(updatedImages);
                      }}
                    >
                      ×
                    </RemoveBtn>
                  </ImageItem>
                ))}
              </UploadedImagesGrid>
            )}

            <FormLabel>
              Upload Image <RequiredStar>*</RequiredStar>
            </FormLabel>
            {allSelectedImages.length < 5 && (
              <UploadZone htmlFor="image-upload-field">
                <UploadIconWrapper>
                  <UploadIconImg src="/images/uploadImage.png" alt="Upload" />
                </UploadIconWrapper>
                <UploadMainText>Click to Upload</UploadMainText>
                <UploadSubText>or drag and upload PNG, JPG or JPEG (max 25mb)</UploadSubText>
                <input
                  type="file"
                  accept="image/jpeg, image/jpg, image/png"
                  id="image-upload-field"
                  onChange={handleImageChange}
                />
              </UploadZone>
            )}

            <FormLabel>
              Reference Link <RequiredStar>*</RequiredStar>
            </FormLabel>
            <InputField
              type="text"
              placeholder="Paste a reference link for your custom cake"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />

            <FormLabel>Text on Cake (Optional)</FormLabel>
            <InputField
              type="text"
              placeholder="What do you want written on the cake?"
              value={textOnCake}
              onChange={(e) => setTextOnCake(e.target.value)}
            />
          </QuestionContent>

          <NavButtonsContainer>
            <NavBtn
              $variant="secondary"
              onClick={handlePreviousStep}
              disabled={activeId === -1}
            >
              ← Previous
            </NavBtn>
            <NavBtn
              onClick={handleNextStep}
              disabled={!isCurrentStepValid()}
            >
              Next Step →
            </NavBtn>
          </NavButtonsContainer>
        </>
      );
    }

    if (activeId >= 0 && activeId < STATIC_QUESTIONS.length) {
      const currentQuestion = STATIC_QUESTIONS[activeId];
      
      return (
        <>
          <QuestionContent>
            <QuestionHeader>{currentQuestion.name}</QuestionHeader>
            <QuestionSubtext>
              {currentQuestion.subtext || (
                currentQuestion?.type == "single" ? (
                  "Choose any 1 option"
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min == 0 && currentQuestion?.max == 0 ? (
                  "Choose multiple options"
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min == 0 && currentQuestion?.max != 0 ? (
                  `Choose maximum ${currentQuestion?.max} options`
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min != 0 && currentQuestion?.max == 0 ? (
                  `Choose minimum ${currentQuestion?.min} options`
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min != 0 && currentQuestion?.max != 0 ? (
                  `Choose ${currentQuestion?.min} to ${currentQuestion?.max} options`
                ) : null
              )}
            </QuestionSubtext>

            {currentQuestion.type === "quantity" ? (
              <OptionsGrid style={{ gridTemplateColumns: '1fr', maxWidth: '600px' }}>
                {currentQuestion?.options.map((option, index) => {
                  const qty = getQuantity(currentQuestion.id, option.id);
                  return (
                    <QuantityOptionBox
                      key={index}
                      $isSelected={qty > 0}
                    >
                      <OptionImageWrapper>
                        <OptionImage
                          src={getOptionImage(option, currentQuestion.id)}
                          alt={option.name}
                          onError={(e) => {
                            e.target.src = '/images/roundCake.png';
                          }}
                        />
                      </OptionImageWrapper>
                      <QuantityControlArea $isSelected={qty > 0}>
                        <QuantityLeftSide>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <OptionName>{option.name}</OptionName>
                            <OptionPriceTag>
                              <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                              {option.price}
                            </OptionPriceTag>
                          </div>
                          <QuantityCounter>
                            <CounterButton
                              onClick={() => handleQuantityDecrement(currentQuestion.id, option.id)}
                              disabled={qty === 0}
                            >
                              −
                            </CounterButton>
                            <CounterValue>{qty}</CounterValue>
                            <CounterButton
                              onClick={() => handleQuantityIncrement(currentQuestion.id, option.id)}
                            >
                              +
                            </CounterButton>
                          </QuantityCounter>
                          {option?.subtext && (
                            <QuantitySubtext>{option.subtext}</QuantitySubtext>
                          )}
                        </QuantityLeftSide>
                      </QuantityControlArea>
                    </QuantityOptionBox>
                  );
                })}
              </OptionsGrid>
            ) : currentQuestion.type === "message" ? (
              <MessageInputBox $hasMessage={hasMessageCard()}>
                <MessageLabel>
                  {currentQuestion.options[0].name}
                  <OptionPriceTag>
                    <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                    {currentQuestion.options[0].price}
                  </OptionPriceTag>
                </MessageLabel>
                <MessageTextarea
                  placeholder="Write your message here..."
                  value={messageCardText}
                  onChange={(e) => setMessageCardText(e.target.value)}
                />
              </MessageInputBox>
            ) : (
              <OptionsGrid>
                {currentQuestion?.options.map((option, index) => (
                  <OptionBox
                    key={index}
                    $isSelected={checkedCheckbox(currentQuestion, option?.id)}
                    onClick={() => addCartItemsState(currentQuestion, option?.id)}
                  >
                    <OptionImageWrapper>
                      <OptionImage
                        src={getOptionImage(option, currentQuestion.id)}
                        alt={option.name}
                        onError={(e) => {
                          e.target.src = '/images/roundCake.png';
                        }}
                      />
                    </OptionImageWrapper>
                    <OptionInfo $isSelected={checkedCheckbox(currentQuestion, option?.id)}>
                      <OptionLeftSide>
                        {currentQuestion?.type == "single" ? (
                          <Radio
                            className="w-5 h-5"
                            checked={checkedCheckbox(currentQuestion, option?.id)}
                            value={option?.id}
                          />
                        ) : (
                          <CheckboxCustom
                            className="w-5 h-5"
                            checked={checkedCheckbox(currentQuestion, option?.id)}
                            value={option?.id}
                            inputClassName="w-5 h-5 !p-0"
                          />
                        )}
                        <OptionTextContent>
                          <OptionName>{option.name}</OptionName>
                          {option?.subtext && (
                            <OptionSubtext>{option.subtext}</OptionSubtext>
                          )}
                        </OptionTextContent>
                      </OptionLeftSide>
                      <OptionPriceTag>
                        <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                        {option.price}
                      </OptionPriceTag>
                    </OptionInfo>
                  </OptionBox>
                ))}
              </OptionsGrid>
            )}
          </QuestionContent>

          <NavButtonsContainer style={{ flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '20px', width: '100%' }}>
              <NavBtn
                $variant="secondary"
                onClick={handlePreviousStep}
              >
                ← Previous
              </NavBtn>
              <NavBtn
                onClick={handleNextStep}
                disabled={!isCurrentStepValid()}
              >
                {activeId === STATIC_QUESTIONS.length - 1 ? "Place Order →" : "Next Step →"}
              </NavBtn>
            </div>

            {/* {activeId === STATIC_QUESTIONS.length - 1 && (
              <NavBtn
                $variant="secondary"
                onClick={() => {
                  if (!auth || auth?.id === 0) {
                    navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
                  } else {
                    saveForLater();
                  }
                }}
                style={{ width: '100%' }}
              >
                🔖 Save for Later
              </NavBtn>
            )} */}
          </NavButtonsContainer>
        </>
      );
    }

    return null;
  };

  useEffect(() => {
    saveToLocalStorage();
  }, [selectedData, allSelectedImages, reference, textOnCake, activeId, completedSteps, totalPrice, selectedShape, quantities, messageCardText]);

  useEffect(() => {
    setTotalPrice(getTotalPrice());
  }, [selectedData, quantities, messageCardText, textOnCake]);

  useEffect(() => {
    loadFromLocalStorage();
    
    const saved = localStorage.getItem("pendingOrderData");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.formData) {
        const formData = parsed.formData;
        setAllInputData(formData);
        
        if (formData.customCakeDetails) {
          setTextOnCake(formData.customCakeDetails.text_on_cake || "");
          setReference(formData.customCakeDetails.image_link || "");
          setAllSelectedImages(formData.customCakeDetails.upload_image_links || []);
          setSelectedData(formData.customCakeDetails.cake_details || []);
        }
        
        setTotalPrice(Number(formData.totalPrice || 0));
      }
    }
  }, []);

  useEffect(() => {
    if (!imageForCrop) {
      const imageUploadField = document.getElementById("image-upload-field");
      if (imageUploadField) {
        imageUploadField.value = "";
      }
    }
  }, [imageForCrop]);

  useEffect(() => {
    CUSTOM.trackCustomCake({ cake_type: "Custom" });
    getQuestionList();
  }, []);

  return (
    <>
      <PageWrapper>
        <GlobalStyle />

        <Header>
          <PageTitle>Customize Your Cake</PageTitle>
          <PageSubtitle>Create a unique cake tailored to your celebration</PageSubtitle>
        </Header>

        {/* Progress Stepper */}
        <StepperSection>
          <StepperWrapper>
            {stepLabels.map((label, index) => {
              const stepIndex = index - 1;
              const isCompleted = completedSteps.includes(stepIndex);
              const isActive = activeId === stepIndex;
              
              return (
                <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                  {index > 0 && <StepLine $isCompleted={completedSteps.includes(stepIndex - 1)} />}
                  <StepContainer>
                    <StepCircle $isCompleted={isCompleted} $isActive={isActive}>
                      {isCompleted && !isActive ? (
                        <StepIconImg 
                          src="/images/checkDone.svg"
                          alt="Completed"
                          $isCompleted={true}
                        />
                      ) : (
                        <StepIconImg 
                          src={getStepIcon(index)}
                          alt={label}
                          $isCompleted={isCompleted}
                        />
                      )}
                    </StepCircle>
                    <StepLabel $isCompleted={isCompleted} $isActive={isActive}>
                      {label}
                    </StepLabel>
                  </StepContainer>
                </div>
              );
            })}
          </StepperWrapper>
        </StepperSection>

        {/* Mobile Step Header - Only visible on mobile */}
        <MobileStepHeader>
          <MobileStepInfo>
            <MobileStepIcon 
              src={getStepIcon(activeId + 1)} 
              alt={getCurrentStepName()}
            />
            <MobileStepText>
              <MobileStepTitle>{getCurrentStepName()}</MobileStepTitle>
              <MobileStepCounter>{getCurrentStepDisplay()}</MobileStepCounter>
            </MobileStepText>
          </MobileStepInfo>
          <ViewDetailsBtn onClick={() => setShowMobilePreview(true)}>
            View Details
          </ViewDetailsBtn>
        </MobileStepHeader>

        <ContentArea>
          <QuestionPanel>
            {renderCurrentStep()}
          </QuestionPanel>

          {/* Desktop Live Preview - Hidden on mobile */}
          <PreviewPanel>
            {renderPreviewContent()}
          </PreviewPanel>
        </ContentArea>

        {/* Mobile Preview Modal */}
        <MobilePreviewModal 
          $isOpen={showMobilePreview}
          onClick={() => setShowMobilePreview(false)}
        >
          <MobilePreviewContent onClick={(e) => e.stopPropagation()}>
            <MobilePreviewHeader>
              <MobilePreviewTitle>Order Details</MobilePreviewTitle>
              <CloseModalBtn onClick={() => setShowMobilePreview(false)}>
                ×
              </CloseModalBtn>
            </MobilePreviewHeader>
            <MobilePreviewBody>
              {renderPreviewContent()}
            </MobilePreviewBody>
          </MobilePreviewContent>
        </MobilePreviewModal>

        {imageForCrop && (
          <ImageCropper
            image={imageForCrop}
            allSelectedImages={allSelectedImages}
            setAllSelectedImages={setAllSelectedImages}
            setImageForCrop={setImageForCrop}
          />
        )}
      </PageWrapper>
      <Footer />
    </>
  );
};

export default DesignMyOwnCakeQuestionOnePage;